'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';

import { useVehicleLicenseList, deleteVehicleLicense } from '../../../stores/VehicleLicenseStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { fleetApi } from '../../../api/axiosClient';
import FilterBuilder from '../../../utils/filterBuilder';
import { useLanguage } from '../../../utils/i18n';

import VehicleLicenseAddEdit from './VehicleLicenseAddEdit';

function VehicleLicensePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  const companyIdString = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const [searchField, setSearchField] = useState('license_name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const { t } = useLanguage();
  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'license_name', label: t('vehicleLicense.licenseName', 'License Name') },
      { value: 'threshold', label: t('vehicleLicense.thresholdWarning', 'Threshold Warning') },
      { value: 'threshold_danger', label: t('vehicleLicense.thresholdDanger', 'Threshold Danger') },
      { value: 'value_periode_notification', label: t('vehicleLicense.periodeNotif', 'Periode Notif') }
    ],
    [t]
  );

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchRaw = searchField === 'all' ? '' : searchValue;

  const {
    data: hookRows,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useVehicleLicenseList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchRaw, companyIdString);

  const [localRows, setLocalRows] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const rows = searchField === 'all' ? localRows : hookRows;
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecord = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(t);
  }, [searchRaw]);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const fb = new FilterBuilder().setPaging(start, length).addJoin('company', ['name']).setOrder(['license_name'], true);

        if (companyIdString) {
          if (companyIdString.includes(',')) {
            fb.in('company_id', companyIdString);
          } else {
            fb.equal('company_id', companyIdString);
          }
        } else {
          fb.filterByCompany();
        }

        const payload = fb.build();

        if (searchValue) {
          payload.filters = [
            { name: 'license_name', logic_operator: 'like', value: searchValue },
            { name: 'threshold', logic_operator: 'like', value: searchValue },
            { name: 'threshold_danger', logic_operator: 'like', value: searchValue },
            { name: 'value_periode_notification', logic_operator: 'like', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchRaw }
          ];
        }

        const res = await fleetApi.post(`/vehicle-license/search?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`, payload, {
          signal: controller.signal
        });
        const data = res.data?.data || res.data || [];
        const total = res.data?.totalRecord ?? res.data?.totalRecord ?? res.data?.dataSize ?? data.length;

        if (!controller.signal.aborted) {
          setLocalRows(Array.isArray(data) ? data : []);
          setLocalTotal(total);
        }
      } catch (err) {
        if (err.name !== 'CanceledError') console.error(err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };
    fetchAllSearch();
    return () => controller.abort();
  }, [searchValue, searchField, companyIdString, paginationModel.page, paginationModel.pageSize, searchRaw]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecord > 0 ? totalRecord : 9999;
      const fb = new FilterBuilder().setPaging(0, length).addJoin('company', ['name']).setOrder(['license_name'], true);

      if (companyIdString) {
        if (companyIdString.includes(',')) {
          fb.in('company_id', companyIdString);
        } else {
          fb.equal('company_id', companyIdString);
        }
      } else {
        fb.filterByCompany();
      }

      const payload = fb.build();

      if (searchValue) {
        if (searchField === 'all') {
          payload.filters = [
            { name: 'license_name', logic_operator: 'like', value: searchValue },
            { name: 'threshold', logic_operator: 'like', value: searchValue },
            { name: 'threshold_danger', logic_operator: 'like', value: searchValue },
            { name: 'value_periode_notification', logic_operator: 'like', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue }
          ];
        } else {
          payload.columns = payload.columns || [];
          payload.columns.push({ name: searchField, logic_operator: 'like', operator: 'AND', value: searchValue });
        }
      } else if (payload.columns && payload.columns.length === 0) {
        payload.columns.push({ name: 'license_name', logic_operator: 'like', operator: 'AND', value: '' });
      }

      const res = await fleetApi.post(`/vehicle-license/search?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`, payload);
      const data = res.data?.data || res.data || [];
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('vehicle license export all error', err);
      return [];
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    if (typeof window !== 'undefined') {
      if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
      else localStorage.removeItem('companyId');
    }
  };
  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteVehicleLicense(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
    } catch (err) {
      console.log(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.2,
      renderCell: (params) => params.row?.join?.company_name || '-'
    },
    { field: 'license_name', headerName: t('vehicleLicense.licenseName', 'License Name'), flex: 1.5 },
    {
      field: 'threshold',
      headerName: t('vehicleLicense.thresholdWarning', 'Threshold Warning'),
      flex: 1,
      valueGetter: (value, params) => `${params.threshold ?? 0} ${t('vehicleLicense.days', 'days')}`
    },
    {
      field: 'threshold_danger',
      headerName: t('vehicleLicense.thresholdDanger', 'Threshold Danger'),
      flex: 1,
      valueGetter: (value, params) => `${params.threshold_danger ?? 0} ${t('vehicleLicense.days', 'days')}`
    },
    {
      field: 'value_periode_notification',
      headerName: t('vehicleLicense.periodeNotif', 'Notif Periode'),
      flex: 1,
      valueGetter: (value, params) =>
        `${params.value_periode_notification ?? ''} ${t(`vehicleLicense.${params.periode_notification}`, params.periode_notification ?? '')}`.trim() ||
        '-'
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onEdit={() => handleEditClick(params.row.id)}
          onDelete={() => handleDelete(params.row.id, params.row.license_name)}
          mode="inline"
        />
      )
    }
  ];
  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  return (
    <div>
      <DataTable
        title={t('vehicleLicense.master', 'Vehicle License')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecord}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />
      <VehicleLicenseAddEdit
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={() => setPaginationModel((p) => ({ ...p }))}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={() => setConfirmOpen(false)}
        itemName={confirmName}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function MasterVehicleLicensePage() {
  return (
    <>
      <VehicleLicensePageContent />
    </>
  );
}
